import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { useTenant } from "@/contexts/TenantContext";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

import { DateRangeFilter, presetRange } from "@/components/reports/DateRangeFilter";
import { BranchFilter } from "@/components/reports/BranchFilter";
import { formatMxn } from "@/components/reports/KpiCard";
import { ExportMenu } from "@/components/reports/ExportMenu";
import { Badge } from "@/components/ui/badge";

export default function TopCustomers() {
  const { isSuperAdmin, activeTenantId } = useTenant();
  const [range, setRange] = useState(() => ({ preset: "month", ...presetRange("month") }));
  const [branchId, setBranchId] = useState("");
  const [sort, setSort] = useState("revenue");
  const [limit, setLimit] = useState(10);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const params = useMemo(() => {
    const p = { sort, limit };
    if (range.date_from) p.date_from = range.date_from;
    if (range.date_to) p.date_to = range.date_to;
    if (branchId) p.branch_id = branchId;
    if (isSuperAdmin && activeTenantId) p.tenant_id = activeTenantId;
    return p;
  }, [range, branchId, sort, limit, isSuperAdmin, activeTenantId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get("/reports/top-customers", { params });
        if (!cancelled) setData(d);
      } catch (_) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [params]);

  const items = data?.items || [];

  return (
    <div className="space-y-6" data-testid="page-top-customers">
      <PageHeader
        title="Top clientes"
        description="Ranking por ingresos o cantidad de órdenes en el período."
        actions={
          <ExportMenu
            endpoint="/reports/top-customers"
            params={params}
            permission="reports:export"
            filename="top-clientes"
            testId="export-top-customers"
          />
        }
      />

      <Card className="border-border">
        <CardContent className="p-4 flex flex-col lg:flex-row gap-4 lg:items-end lg:justify-between">
          <DateRangeFilter value={range} onChange={setRange} />
          <div className="flex flex-wrap items-end gap-3">
            <BranchFilter value={branchId} onChange={setBranchId} />
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                Ordenar por
              </div>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="h-9 w-40" data-testid="topcust-sort">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="revenue">Ingresos</SelectItem>
                  <SelectItem value="orders">Cantidad órdenes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                Top
              </div>
              <Select value={String(limit)} onValueChange={(v) => setLimit(Number(v))}>
                <SelectTrigger className="h-9 w-24" data-testid="topcust-limit">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[5, 10, 20, 50].map((n) => (
                    <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border" data-testid="topcust-table">
        <CardHeader className="border-b border-border">
          <CardTitle className="tracking-tight">Clientes con mayor {sort === "revenue" ? "ingreso" : "actividad"}</CardTitle>
          <CardDescription>{items.length} resultados.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-2">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-8" />)}
            </div>
          ) : items.length ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">#</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="text-right">Órdenes</TableHead>
                  <TableHead className="text-right">Ingresos</TableHead>
                  <TableHead className="text-right">Ticket prom.</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((c, idx) => (
                  <TableRow key={c.customer_id} data-testid={`topcust-row-${c.customer_id}`}>
                    <TableCell className="text-muted-foreground text-xs">{idx + 1}</TableCell>
                    <TableCell className="font-medium">{c.legal_name}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="capitalize text-[10px]">
                        {c.customer_type || "—"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">{c.orders}</TableCell>
                    <TableCell className="text-right font-semibold">{formatMxn(c.revenue)}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{formatMxn(c.avg_ticket)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="p-6 text-sm text-muted-foreground">Sin ventas en el período.</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
